import AgeLabel from '@monorepo/common/component/AgeLabel';
import { AppConfigContext } from '@monorepo/common/component/AppConfigProvider';
import ClientNameLabel from '@monorepo/common/component/client/ClientNameLabel';
import DateLabel from '@monorepo/common/component/DateLabel';
import { IconRegularEdit, IconSolidPlus, IconSolidPrint, IconSolidUserPlus } from '@monorepo/common/component/Icons';
import PhoneLabel from '@monorepo/common/component/PhoneLabel';
import EmailLabel from '@monorepo/common/component/EmailLabel';
import RegistrationInfoLabel from '@monorepo/common/component/RegistrationInfoLabel';
import Secured, { ROLE_ADMIN_CLIENT_DELETE } from '@monorepo/common/component/Secured';
import SexLabel from '@monorepo/common/component/SexLabel';
import React, { ReactNode, useContext } from 'react';
import { Button, Col, ColProps, CustomInput, Row } from 'reactstrap';
import { AccompanistFragment, OrderTotalStatusEnum, SokolRoleEnum, StampTypeEnum } from '../../graphql-operations';
import { getServerBasename } from '../../util/Url';
import ClientAccompanistList from '../accompanist/ClientAccompanistList';
import CovidAccountBalance from '../covid/CovidAccountBalance';
import InsuranceReceiptList from '../insuranceReceipt/InsuranceReceiptList';
import LegalRepresentationList, { LegalRepresentationType } from '../legalRepresentation/LegalRepresentationList';
import PaymentDisciplineForClient from '../payment/discipline/PaymentDisciplineForClient';
import PaymentStampList from '../payment/stamp/PaymentStampList';
import PaymentTokenList from '../payment/token/PaymentTokenList';
import PersonSwitchNameLink from '../person/switch/PersonSwitchNameLink';
import SokolRoleList from '../sokolRole/SokolRoleList';
import ClientDetailDeleteModal from './ClientDetailDeleteModal';
import ClientDetailEditModal from './ClientDetailEditModal';
import ClientGdprAgreementNewModal from './ClientGdprAgreementNewModal';

interface ClientRowProps {
  label: string;
  value?: ReactNode;
  children?: ReactNode;
}

function ClientRow({ label, value, children }: ClientRowProps) {
  return (
    <Row>
      <Col xs={5}>
        <span className="text-muted">{label}</span>
      </Col>
      <Col xs={7}>{children || value}</Col>
    </Row>
  );
}

function ClientDoubleRow({ label, value, children }: ClientRowProps) {
  return (
    <Row>
      <Col xs={12}>
        <span className="text-muted">{label}</span>
      </Col>
      <Col xs={12}>{children || value}</Col>
    </Row>
  );
}

export function NewClientIcon() {
  return (
    <ClientDetailEditModal title="Nový člen">
      {(handleModalOpen, title) => (
        <h2>
          <IconSolidUserPlus onClick={handleModalOpen} title={title} className="action-icon text-primary" />
        </h2>
      )}
    </ClientDetailEditModal>
  );
}

interface ModuleColProps extends ColProps {
  children: ReactNode;
}

/** Adds margin-top */
export function ModuleCol({ children, ...props }: ModuleColProps) {
  return (
    <Col {...props} className="mt-4">
      {children}
    </Col>
  );
}

export interface ClientType {
  __typename?: 'Client';
  id: string;
  personData: {
    __typename?: 'PersonData';
    id: string;
    degreeBefore?: string | null;
    degreeAfter?: string | null;
    firstName: string;
    lastName: string;
    sex?: string | null;
    phone?: number | null;
    phone2?: number | null;
    email?: string | null;
    note?: string | null;
    personalDataVerified: boolean;
    legalRepresentationsChilds: LegalRepresentationType[];
  };
  clientData: {
    __typename?: 'ClientData';
    id: string;
    birthNumber: string;
    age: number;
    street: string;
    streetNumberO?: string | null;
    streetNumberP?: string | null;
    streetNumberJoined: string;
    city: string;
    zip: number;
    mcp4: boolean;
    birthday: string;
    ccp?: string | null;
    cardId?: string | null;
    citizenship: {
      __typename?: 'Citizenship';
      id: string;
      name: string;
    };
    gdprAgreements: Array<{ __typename?: 'ClientGdprAgreement'; id: string; signDateTime: string }>;
    paymentsStamp: Array<{
      __typename?: 'PaymentStamp';
      id: string;
      year: number;
      type: StampTypeEnum;
      orderStamp: {
        __typename?: 'OrderStamp';
        id: string;
        priceDiscounted: number;
        orderTotal: { __typename?: 'OrderTotal'; id: string; orderNumber: string };
      };
    }>;
    paymentsDiscipline: Array<{
      __typename?: 'PaymentDiscipline';
      id: string;
      disciplineConfig: {
        __typename?: 'DisciplineConfig';
        id: string;
        discipline: { __typename?: 'Discipline'; id: string; name: string; insuranceReceiptPurpose?: string | null };
        period: { __typename?: 'Period'; id: string; year: number; number: number };
      };
      orderDiscipline: {
        __typename?: 'OrderDiscipline';
        id: string;
        priceDiscounted: number;
        orderTotal: { __typename?: 'OrderTotal'; id: string; orderNumber: string };
      };
      insuranceReceipt?: { __typename?: 'InsuranceReceipt'; id: string } | null;
    }>;
    paymentsToken: Array<{
      __typename?: 'PaymentToken';
      id: string;
      token: {
        __typename?: 'Token';
        id: string;
        name: string;
        period: { __typename?: 'Period'; id: string; year: number; number: number };
      };
      orderToken: {
        __typename?: 'OrderToken';
        id: string;
        price: number;
        orderTotal: {
          __typename?: 'OrderTotal';
          id: string;
          orderNumber: string;
        };
      };
    }>;
    sokolRoles: Array<{
      __typename?: 'SokolRole';
      id: string;
      role: SokolRoleEnum;
      period: { __typename?: 'Period'; id: string; year: number; number: number };
    }>;
    accompanists: AccompanistFragment[];
    accompanistChilds: AccompanistFragment[];
    insuranceReceipts: Array<{
      __typename?: 'InsuranceReceipt';
      id: string;
      receiptId: string;
      created: string;
      purpose: string;
      amount: number;
      period: { __typename?: 'Period'; id: string; year: number; number: number };
      paymentDiscipline?: {
        __typename?: 'PaymentDiscipline';
        id: string;
        disciplineConfig: {
          __typename?: 'DisciplineConfig';
          id: string;
          discipline: { __typename?: 'Discipline'; id: string; name: string };
          period: { __typename?: 'Period'; id: string; year: number; number: number };
        };
        orderDiscipline: {
          __typename?: 'OrderDiscipline';
          id: string;
          priceDiscounted: number;
          orderTotal: { __typename?: 'OrderTotal'; id: string; orderNumber: string };
        };
      } | null;
    }>;
    legalRepresentationsRepresentatives: LegalRepresentationType[];
    covidAccountBalanceInfo: {
      __typename?: 'CovidAccountBalanceInfo';
      covidAccountBalance: number;
      ordersDiscipline: Array<{
        __typename?: 'OrderDiscipline';
        id: string;
        priceOrig: number;
        priceDiscounted: number;
        covidAccount: number;
        orderTotal: { __typename?: 'OrderTotal'; id: string; orderNumber: string; status: OrderTotalStatusEnum };
        disciplineConfig: {
          __typename?: 'DisciplineConfig';
          id: string;
          period: { __typename?: 'Period'; id: string; year: number; number: number };
          discipline: { __typename?: 'Discipline'; id: string; name: string };
        };
      }>;
    };
  };
}

interface ClientDetailViewProps {
  client: ClientType;
}

export default function ClientDetailView({ client }: ClientDetailViewProps) {
  const { personData, clientData } = client;
  const { currentPeriod } = useContext(AppConfigContext);

  if (!currentPeriod) {
    return null;
  }

  return (
    <>
      <Row>
        <Col>
          <ClientDetailEditModal key={client.id} title="Změna člena" client={client}>
            {(handleModalOpen, title) => (
              <h2>
                <IconRegularEdit onClick={handleModalOpen} title={title} className="action-icon text-primary" />
                <ClientNameLabel client={client} />{' '}
                <span className="text-muted">
                  (<AgeLabel age={clientData.age} />)
                </span>
              </h2>
            )}
          </ClientDetailEditModal>
        </Col>
      </Row>

      {/* client info */}
      <Row>
        <Col lg={6}>
          <ClientRow label="Rodné číslo" value={clientData.birthNumber} />
          <ClientRow label="Ulice" value={`${clientData.street} ${clientData.streetNumberJoined}`} />
          <ClientRow label="Město" value={clientData.city} />
          <ClientRow label="PSČ" value={clientData.zip} />
          <ClientRow label="Občanství" value={clientData.citizenship.name} />
          <ClientRow label="Občan MČP 4">
            <CustomInput id="mcp4" type="switch" disabled checked={clientData.mcp4} />
          </ClientRow>
          {/*@ts-ignore*/}
          <ClientRow label="Pohlaví">{personData.sex && <SexLabel sex={personData.sex} />}</ClientRow>
          <ClientRow label="Datum narození">
            <DateLabel dateString={clientData.birthday} />
          </ClientRow>

          <hr />

          <ClientRow label="Telefon">
            <PhoneLabel phoneNumber={personData.phone} />
          </ClientRow>

          <ClientRow label="Telefon2">
            <PhoneLabel phoneNumber={personData.phone2} />
          </ClientRow>

          <ClientDoubleRow label="E-mail">
            <EmailLabel email={personData.email} />
          </ClientDoubleRow>
        </Col>

        <Col lg={6}>
          <hr className="d-xs-block d-lg-none" />
          <ClientRow label="Registrace">
            <RegistrationInfoLabel personalDataVerified={personData.personalDataVerified} />
          </ClientRow>
          <ClientRow label="Cvičitelský průkaz" value={clientData.ccp} />
          <ClientRow label="Členský průkaz" value={clientData.cardId} />

          <ClientRow label="Souhlasy GDPR">
            <>
              {clientData.gdprAgreements.map(gdprAgreement => (
                <div key={gdprAgreement.id}>
                  <DateLabel dateString={gdprAgreement.signDateTime} />
                  &nbsp;
                  <a
                    href={getServerBasename() + `/rest/export/client/card-gdpr-adult/${gdprAgreement.id}`}
                    title="Tisk karty člena"
                    download
                  >
                    <IconSolidPrint />
                  </a>
                </div>
              ))}
              {clientData.legalRepresentationsRepresentatives.map(legalRepresentation =>
                legalRepresentation.gdprAgreements.map(gdprAgreement => (
                  <div key={gdprAgreement.id}>
                    <DateLabel dateString={gdprAgreement.signDateTime} />
                    &nbsp;
                    <a
                      href={
                        getServerBasename() + `/rest/export/client/card-gdpr-legal-representation/${gdprAgreement.id}`
                      }
                      title="Tisk karty člena"
                      download
                    >
                      <IconSolidPrint />
                    </a>{' '}
                    (<PersonSwitchNameLink person={legalRepresentation.representative} />)
                  </div>
                ))
              )}
              {clientData.age >= 18 && (
                <div>
                  <ClientGdprAgreementNewModal key={client.id} title="Udělení GDPR souhlasu" clientId={client.id}>
                    {(handleModalOpen, title) => (
                      <IconSolidPlus onClick={handleModalOpen} title={title} className="action-icon text-primary" />
                    )}
                  </ClientGdprAgreementNewModal>
                </div>
              )}
            </>
          </ClientRow>
          <ClientDoubleRow label="Poznámka" value={personData.note} />
        </Col>
      </Row>

      {/* Modules */}
      <Row>
        <ModuleCol xl={8}>
          <PaymentDisciplineForClient
            key={client.id}
            clientId={client.id}
            paymentsDiscipline={clientData.paymentsDiscipline}
          />
        </ModuleCol>

        <ModuleCol xl={4}>
          <PaymentStampList key={client.id} paymentsStamp={clientData.paymentsStamp} clientId={client.id} />
        </ModuleCol>

        {clientData.covidAccountBalanceInfo.ordersDiscipline.length > 0 && (
          <ModuleCol>
            <CovidAccountBalance key={client.id} covidAccountBalanceInfo={clientData.covidAccountBalanceInfo} />
          </ModuleCol>
        )}

        <ModuleCol xl={8}>
          <LegalRepresentationList
            child={clientData.age < 18 ? client : undefined}
            representative={clientData.age >= 18 ? client : undefined}
            legalRepresentationsChilds={personData.legalRepresentationsChilds}
            legalRepresentationsRepresentatives={clientData.legalRepresentationsRepresentatives}
          />
        </ModuleCol>

        <ModuleCol lg={6} xl={4}>
          <SokolRoleList key={client.id} sokolRoles={clientData.sokolRoles} clientId={client.id} />
        </ModuleCol>

        <ModuleCol lg={6}>
          <ClientAccompanistList
            accompanists={clientData.accompanists}
            accompanistChilds={clientData.accompanistChilds}
            clientId={client.id}
            allowNew={clientData.age < 17}
          />
        </ModuleCol>

        <ModuleCol xl={6}>
          <InsuranceReceiptList key={client.id} clientId={client.id} insuranceReceipts={clientData.insuranceReceipts} />
        </ModuleCol>

        <ModuleCol lg={6}>
          <PaymentTokenList paymentsToken={clientData.paymentsToken} clientId={client.id} />
        </ModuleCol>
      </Row>

      <Row className="mb-3">
        <ModuleCol>
          <Secured authority={ROLE_ADMIN_CLIENT_DELETE}>
            <ClientDetailDeleteModal key={client.id} client={client}>
              {(handleModalOpen, title) => (
                <Button color="danger" onClick={handleModalOpen} title={title}>
                  Smazání člena
                </Button>
              )}
            </ClientDetailDeleteModal>
          </Secured>
        </ModuleCol>
      </Row>
    </>
  );
}
